import React from 'react';
import { Trophy, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * LeagueBadge - Displays the league icon with color
 */
export function LeagueBadge({ league, size = 'md', showName = false }) {
    const leagueConfig = {
        bronze: { name: 'Bronze', color: '#CD7F32', bg: 'bg-amber-900/20' },
        silver: { name: 'Argent', color: '#C0C0C0', bg: 'bg-gray-300/20' },
        gold: { name: 'Or', color: '#FFD700', bg: 'bg-yellow-400/20' },
        platinum: { name: 'Platine', color: '#E5E4E2', bg: 'bg-slate-300/20' },
        diamond: { name: 'Diamant', color: '#B9F2FF', bg: 'bg-cyan-300/20' }
    };

    const config = leagueConfig[league] || leagueConfig.bronze;
    const sizeClasses = {
        sm: 'w-5 h-5',
        md: 'w-6 h-6',
        lg: 'w-8 h-8'
    };

    return (
        <div className={cn('inline-flex items-center gap-1.5', showName && 'px-2 py-1 rounded-full', showName && config.bg)}>
            <Trophy className={sizeClasses[size]} style={{ color: config.color }} />
            {showName && <span className="text-sm font-medium" style={{ color: config.color }}>{config.name}</span>}
        </div>
    );
}

/**
 * EloDisplay - Shows Elo score with league badge and trend
 */
export function EloDisplay({ elo, league, leagueInfo, trend, size = 'md', showProgress = false }) {
    const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
    const trendColor = trend === 'up' ? 'text-green-400' : trend === 'down' ? 'text-red-400' : 'text-muted-foreground';

    const sizeClasses = {
        sm: { elo: 'text-lg', label: 'text-xs' },
        md: { elo: 'text-2xl', label: 'text-sm' },
        lg: { elo: 'text-4xl', label: 'text-base' }
    };

    return (
        <div className="flex items-center gap-3">
            <LeagueBadge league={league} size={size} />
            <div>
                <div className="flex items-center gap-2">
                    <span className={cn('font-bold text-foreground', sizeClasses[size].elo)}>{Math.round(elo)}</span>
                    <TrendIcon className={cn('w-4 h-4', trendColor)} />
                </div>
                {leagueInfo && (
                    <div className={cn('text-muted-foreground', sizeClasses[size].label)}>
                        {leagueInfo.league_name}
                        {leagueInfo.next_league_name && (
                            <span className="ml-1">
                                • {leagueInfo.points_to_next} pts → {leagueInfo.next_league_name}
                            </span>
                        )}
                    </div>
                )}
            </div>
            {showProgress && leagueInfo?.next_league && (
                <div className="flex-1 max-w-32">
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                            className="h-full bg-gradient-to-r from-[#681793] to-[#FF914D] transition-all"
                            style={{ width: `${leagueInfo.progress_percent}%` }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * EloCard - Full card display for dashboard
 */
export function EloCard({ stats, className }) {
    if (!stats) {
        return (
            <div className={cn('p-6 rounded-2xl border border-border bg-card backdrop-blur-sm', className)}>
                <div className="text-center text-muted-foreground">
                    <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-3">
                        <Trophy className="w-7 h-7 text-muted-foreground" />
                    </div>
                    <p className="text-sm">Recevez votre premier avis pour démarrer votre classement</p>
                </div>
            </div>
        );
    }

    return (
        <div className={cn('p-6 rounded-2xl border border-border bg-card backdrop-blur-sm', className)}>
            <div className="flex items-start justify-between mb-4">
                <h3 className="font-bold text-lg text-foreground">Votre classement</h3>
                <LeagueBadge league={stats.league} size="lg" />
            </div>
            
            <div className="space-y-4">
                <EloDisplay 
                    elo={stats.elo} 
                    league={stats.league} 
                    leagueInfo={stats.league_info}
                    trend={stats.trend}
                    size="lg"
                    showProgress
                />

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                    <div className="p-3 rounded-xl bg-muted">
                        <p className="text-2xl font-bold text-foreground">{stats.total_ratings}</p>
                        <p className="text-xs text-muted-foreground">Avis reçus</p>
                    </div>
                    <div className="p-3 rounded-xl bg-muted">
                        <p className="text-2xl font-bold text-foreground">{stats.avg_rating?.toFixed(1) || '—'}/5</p>
                        <p className="text-xs text-muted-foreground">Note moyenne</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EloDisplay;
